<template>
	<div
		class="event-center-navigation"
		:class="{'-has-mobile-competition-select': showMobileCompetitionSelect}"
	>
		<div
			v-if="showScopeTabs"
			class="event-center-navigation__row -scope"
		>
			<InPageNavigation
				:nav-items="resolvedScopeNavItems"
				:use-anchor-tag="false"
				:with-line="false"
				:with-margin-bottom="false"
				:line-column-width="false"
				:dark-theme="!$arenaConfig.isLightTheme"
				horizontal
				@nav-change="$emit('scope-change', $event)"
			/>
		</div>

		<div
			v-if="showMobileCompetitionSelect"
			class="event-center-navigation__row -competition-mobile"
		>
			<Select
				class="event-center-navigation__competition-select -mobile"
				:options="resolvedCompetitionOptions"
				:value="selectedCompetitionValue"
				:searchable="false"
				:clearable="false"
				:dark-theme="!$arenaConfig.isLightTheme"
				:rounded="true"
				@change="$emit('competition-change', $event)"
			/>
		</div>

		<div
			v-if="isCompetitionView"
			class="event-center-navigation__row -competition"
		>
			<InPageNavigation
				:nav-items="resolvedCompetitionContentNavItems"
				:use-anchor-tag="false"
				:with-line="false"
				:with-margin-bottom="false"
				:line-column-width="false"
				:dark-theme="!$arenaConfig.isLightTheme"
				size="sm"
				horizontal
				@nav-change="$emit('competition-content-change', $event)"
			/>

			<Select
				class="event-center-navigation__competition-select"
				:options="resolvedCompetitionOptions"
				:value="selectedCompetitionValue"
				:searchable="false"
				:clearable="false"
				:dark-theme="!$arenaConfig.isLightTheme"
				:rounded="true"
				@change="$emit('competition-change', $event)"
			/>
		</div>

		<div
			v-if="showViewNavigationRow"
			class="event-center-navigation__row -view"
		>
			<div class="event-center-navigation__view-navigation">
				<ViewSelect
					v-if="isCompetitionView"
					class="event-center-navigation__view-select"
					:options="competitionSubviewOptions"
					:value="selectedCompetitionSubviewValue"
					@change="$emit('competition-subview-change', $event)"
				/>

				<ViewSelect
					v-else
					class="event-center-navigation__view-select"
					:options="allEventsOptions"
					:value="selectedAllEventsValue"
					@change="$emit('all-events-view-change', $event)"
				/>
			</div>

			<Select
				v-if="shouldShowSubStageSelect"
				class="event-center-navigation__substage-select"
				:options="resolvedSubStageOptions"
				:value="resolvedSubStageValue"
				:searchable="false"
				:clearable="false"
				:dark-theme="!$arenaConfig.isLightTheme"
				:rounded="true"
				@change="$emit('substage-change', $event)"
			/>
		</div>

		<div
			v-if="showFiltersRow"
			class="event-center-navigation__row -filters"
		>
			<NavigationFilters
				:filters="resolvedActiveFilters"
				:show-search="resolvedShowSearch"
				:search-value="queryState.q || ''"
				:extra-mobile-filters="mobileExtraFilters"
				:filter-toggle-extra-top-offset="showMobileCompetitionSelect"
				@search-change="$emit('search-change', $event)"
				@filter-change="handleSharedFilterChange"
			/>
		</div>
	</div>
</template>

<script>
import NavigationFilters from '@js/components/NavigationFilters.vue';
import InPageNavigation from '@shared/components/InPageNavigation';
import Select from '@shared/components/Select';
import ViewSelect from '@js/components/ViewSelect';

export default {
	name:       'EventCenterNavigation',
	components: {
		InPageNavigation,
		NavigationFilters,
		Select,
		ViewSelect,
	},
	props: {
		showScopeTabs: {
			type:    Boolean,
			default: false,
		},
		navigationConfig: {
			type:    Object,
			default: null,
		},
		isCompetitionView: {
			type:    Boolean,
			default: false,
		},
		showSearch: {
			type:    Boolean,
			default: true,
		},
		showSelectFilters: {
			type:    Boolean,
			default: true,
		},
		queryState: {
			type:     Object,
			required: true,
		},
		scopeNavItems: {
			type:    Array,
			default: () => [],
		},
		allEventsNavItems: {
			type:    Array,
			default: () => [],
		},
		competitionOptions: {
			type:    Array,
			default: () => [],
		},
		competitionContentNavItems: {
			type:    Array,
			default: () => [],
		},
		subStageOptions: {
			type:    Array,
			default: () => [],
		},
		subStageValue: {
			type:    [String, Number, null],
			default: null,
		},
		activeFilters: {
			type:    Array,
			default: () => [],
		},
	},
	emits: [
		'scope-change',
		'competition-change',
		'competition-subview-change',
		'competition-content-change',
		'substage-change',
		'all-events-view-change',
		'search-change',
		'filter-change',
	],
	computed: {
		selectedScope() {
			return this.queryState.scope || (this.isCompetitionView ? 'competitions' : 'all-events');
		},
		resolvedScopeNavItems() {
			if (!this.navigationConfig?.scopeTabs?.length) {
				return this.scopeNavItems;
			}

			return this.navigationConfig.scopeTabs
				.filter(item => item.enabled !== false)
				.map(item => ({
					label:  item.label,
					url:    item.value,
					active: item.value === this.selectedScope,
				}));
		},
		resolvedAllEventsNavItems() {
			if (!this.navigationConfig?.allEvents?.views?.length) {
				return this.allEventsNavItems;
			}

			return this.navigationConfig.allEvents.views
				.filter(item => item.enabled !== false)
				.map(item => ({
					label:  item.label,
					url:    item.value,
					active: item.value === this.queryState.view,
				}));
		},
		allEventsOptions() {
			return this.resolvedAllEventsNavItems.map(item => ({
				label: item.label,
				value: item.url,
			}));
		},
		selectedAllEventsValue() {
			return this.queryState.view || this.allEventsOptions[0]?.value || null;
		},
		resolvedCompetitionOptions() {
			const options = this.navigationConfig?.competitions?.options || this.competitionOptions;
			return options.filter(option => option.enabled !== false);
		},
		selectedCompetitionValue() {
			return this.queryState.competition || this.resolvedCompetitionOptions[0]?.value || null;
		},
		selectedCompetitionConfig() {
			return this.navigationConfig?.competitions?.byCompetitionId?.[this.selectedCompetitionValue] || null;
		},
		resolvedCompetitionContentNavItems() {
			return this.competitionContentNavItems.filter(item => item.enabled !== false);
		},
		resolvedCompetitionSubviewNavItems() {
			if (!this.selectedCompetitionConfig?.subviews?.length) {
				return [];
			}

			const selectedSubview = this.queryState.subview || this.selectedCompetitionConfig.subviews[0]?.value || null;

			return this.selectedCompetitionConfig.subviews
				.filter(item => item.enabled !== false)
				.map(item => ({
					label:  item.label,
					url:    item.value,
					active: item.value === selectedSubview,
				}));
		},
		competitionSubviewOptions() {
			return this.resolvedCompetitionSubviewNavItems.map(item => ({
				label: item.label,
				value: item.url,
			}));
		},
		selectedCompetitionSubview() {
			return this.queryState.subview || this.resolvedCompetitionSubviewNavItems.find(item => item.active)?.url || this.resolvedCompetitionSubviewNavItems[0]?.url || null;
		},
		selectedCompetitionSubviewValue() {
			return this.selectedCompetitionSubview || this.competitionSubviewOptions[0]?.value || null;
		},
		selectedCompetitionContentValue() {
			return this.resolvedCompetitionContentNavItems.find(item => item.active)?.url || 'fixtures-results';
		},
		isCompetitionFeatureViewActive() {
			return this.isCompetitionView && this.selectedCompetitionContentValue !== 'fixtures-results';
		},
		resolvedSubStageOptions() {
			if (!this.selectedCompetitionConfig?.subStagesBySubview) {
				return this.subStageOptions;
			}

			return this.selectedCompetitionConfig.subStagesBySubview[this.selectedCompetitionSubview] || [];
		},
		resolvedSubStageValue() {
			return this.queryState.substage ?? this.subStageValue ?? this.resolvedSubStageOptions[0]?.value ?? null;
		},
		resolvedActiveFilters() {
			const filters = this.isCompetitionView
				? this.selectedCompetitionConfig?.filters || []
				: this.navigationConfig?.allEvents?.filters || [];

			const mappedFilters = filters
				.filter(filter => filter.enabled !== false && filter.control === 'select')
				.map(filter => ({
					key:          filter.key,
					options:      filter.options || [],
					value:        this.queryState[filter.key] ?? filter.options?.[0]?.value ?? null,
					defaultValue: filter.options?.[0]?.value ?? null,
				}))
				.filter(filter => Boolean(filter.key));

			return mappedFilters.length > 0 ? mappedFilters : this.activeFilters;
		},
		resolvedShowSearch() {
			const filters = this.isCompetitionView
				? this.selectedCompetitionConfig?.filters || []
				: this.navigationConfig?.allEvents?.filters || [];

			if (filters.length === 0) {
				return this.showSearch;
			}

			const searchFilter = filters.find(filter => filter.control === 'search');
			return this.showSearch && searchFilter?.enabled !== false;
		},
		showViewNavigationRow() {
			if (this.isCompetitionFeatureViewActive) {
				return false;
			}

			if (this.isCompetitionView) {
				return this.competitionSubviewOptions.length > 1 || this.shouldShowSubStageSelect;
			}

			return this.allEventsOptions.length > 1;
		},
		shouldShowSubStageSelect() {
			return this.isCompetitionView && this.resolvedSubStageOptions.length > 0;
		},
		showMobileCompetitionSelect() {
			return this.isCompetitionView && this.resolvedCompetitionOptions.length > 1;
		},
		showFiltersRow() {
			if (this.isCompetitionFeatureViewActive) {
				return false;
			}

			const hasSelectFilters = this.showSelectFilters && this.resolvedActiveFilters.length > 0;
			return hasSelectFilters || this.resolvedShowSearch;
		},
		mobileSubStageFilter() {
			if (!this.shouldShowSubStageSelect) {
				return null;
			}

			return {
				type:         'select',
				id:           'substage',
				label:        this.$I18n.trans_choice('commons.sub_stage', 1),
				defaultValue: this.resolvedSubStageOptions[0]?.value ?? null,
				value:        this.resolvedSubStageValue,
				options:      this.resolvedSubStageOptions,
			};
		},
		mobileExtraFilters() {
			return this.mobileSubStageFilter ? [this.mobileSubStageFilter] : [];
		},
	},
	methods: {
		handleSharedFilterChange({ key, value }) {
			if (key === 'substage') {
				this.$emit('substage-change', value);
				return;
			}

			this.$emit('filter-change', { key, value });
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@sass/variables';

.event-center-navigation {
	position: relative;
}

.event-center-navigation__row {
	display: flex;
	align-items: center;
	gap: $sp2;
	margin-bottom: $sp4-5;

	&.-scope {
		margin-bottom: $sp5;
	}

	&.-competition {
		justify-content: space-between;
	}

	&.-competition-mobile {
		@include size(md-up) {
			display: none;
		}
	}

	&.-view {
		display: flex;
		flex-wrap: wrap;
		gap: $sp4;
		justify-content: space-between;

		@include size(md-up) {
			min-height: 52px;
		}
	}

	&.-filters {
		display: block;

		@include size(md-up) {
			margin-bottom: $sp6;
		}
	}

	@include size(md-up) {
		margin-bottom: $sp3-5;
	}
}

.event-center-navigation__competition-select,
.event-center-navigation__substage-select {
	display: none;
	width: 100%;
	flex: 1 1 auto;
	margin-bottom: 0;

	@include size(md-up) {
		display: block;
		width: 300px;
		flex: 0 0 300px;
	}
}

.event-center-navigation__competition-select.-mobile {
	display: block;
	width: calc(100% - 50px);
	flex-grow: 0;

	@include size(md-up) {
		display: none;
	}
}

.event-center-navigation__view-navigation {
	display: flex;
	align-items: center;
	gap: $sp2;
	flex: 1 1 auto;
	min-width: 0;
}

.event-center-navigation__view-select {
	flex: 1 1 auto;
	min-width: 0;
	margin-bottom: 0;
}

</style>
